import { History } from "lucide-react";
import { getApi } from "../../api";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { Card } from "../Card";
import { Skeleton } from "../ScreenStates";

interface PriorYearRatingsCardProps {
  groupId: string;
  memberId: string;
  children?: never;
}

/** Prior-year ratings from EDW for renewal members. Renders nothing when the member has none. */
export function PriorYearRatingsCard({ groupId, memberId }: PriorYearRatingsCardProps) {
  const prior = useAsyncResource(() => getApi().getMemberPriorYearRatings(groupId, memberId), `${groupId}:${memberId}`, "PriorYearRatingsCard.load");
  const { state } = prior;

  if (state.kind === "error") return null;
  if (state.kind === "loading" && !state.data) {
    return (
      <div role="status" aria-label="Loading prior-year ratings" className="rounded-2xl bg-white p-4 shadow-md">
        <Skeleton className="h-4 w-40" />
      </div>
    );
  }
  if (!state.data || state.data.length === 0) return null;

  return (
    <Card title={`Prior-year ratings (${state.data[0].planYear})`} icon={History}>
      <table className="w-full text-[13px]">
        <caption className="sr-only">Ratings assigned in the prior plan year</caption>
        <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
          <tr className="border-b border-gray-200">
            <th scope="col" className="px-2 py-1">Condition</th>
            <th scope="col" className="px-2 py-1">Rating</th>
            <th scope="col" className="px-2 py-1 text-right">Debits</th>
          </tr>
        </thead>
        <tbody>
          {state.data.map((rating) => (
            <tr key={`${rating.conditionName}-${rating.ratingText}`} className="border-b border-gray-100">
              <td className="px-2 py-1 text-gray-800">{rating.conditionName}</td>
              <td className="px-2 py-1 text-gray-700">{rating.ratingText}</td>
              <td className="px-2 py-1 text-right tabular-nums">{rating.debits}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
